import React, { useEffect, useRef, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { FormState } from 'final-form';
import { Box, Button, Grid, Typography } from 'housecall-ui';

import { FormField } from '../../src/components/form/FormField';
import { trackButtonClicked } from '../../src/hooks/useEventTracking';
import { AddressFields } from '../../src/Steps/ContactDetailsStep/AddressFields';
import { MarketingConsent } from '../../src/Steps/ContactDetailsStep/MarketingConsenst';
import { useToastController } from '../../src/toast';
import { statusCodesMapper } from '../../src/utils/exception';
import { useLeadCaptureHeight } from '../hooks/useLeadCaptureHeight';
import { useLeadCaptureSettings } from '../hooks/useLeadCaptureSettings';
import { useLeadCreate } from '../hooks/useLeadCreate';
import { FieldName, LeadCaptureFormFields, LeadFormValues, StaleLeadCaptureFormFields } from '../types/types';
import { onValidate } from '../validation/validation';

import { Footer } from './Footer';

export const DEFAULT_COUNTRY = 'United States';

const STALE_LEAD_FORM_FIELDS = ['capture_first_name', 'capture_phone_number'];

export const FIELD_LABEL_MAP: Partial<Record<keyof LeadCaptureFormFields, string>> &
  Partial<Record<keyof StaleLeadCaptureFormFields, string>> = {
  capture_address: 'Address',
  capture_custom_question: 'Custom question',
  capture_email: 'Email',
  capture_frequency: 'Frequency',
  capture_how_did_you_hear_about_us: 'How did you hear about us?',
  capture_last_name: 'Last name',
  capture_first_name: 'First name',
  capture_phone_number: 'Phone number',
  capture_service_details: 'Service details',
  capture_file_upload: 'File upload',
};

const FIELDS_ORDER: (keyof LeadCaptureFormFields | keyof StaleLeadCaptureFormFields)[] = [
  'capture_first_name',
  'capture_last_name',
  'capture_phone_number',
  'capture_email',
  'capture_address',
  'capture_service_details',
  'capture_how_did_you_hear_about_us',
  'capture_frequency',
  'capture_custom_question',
  'capture_file_upload',
];

const DISABLED_FIELDS = ['capture_file_upload', 'custom_question_label'];

const renderDefaultField = (field: keyof LeadCaptureFormFields | keyof StaleLeadCaptureFormFields) => {
  return (
    <Grid item xs={12} key={field}>
      <FormField<FieldName>
        key={field}
        name={field as FieldName}
        data-testid={`lead-form__${field}`}
        label={FIELD_LABEL_MAP[field as keyof LeadCaptureFormFields]}
        fullWidth
      />
    </Grid>
  );
};

export const FormContent = () => {
  const updateHeight = useLeadCaptureHeight();
  const { data } = useLeadCaptureSettings();
  const formRef = useRef<HTMLFormElement>(null);
  const { mutateAsync, isLoading: isLoadingMutation } = useLeadCreate();
  const { showToast } = useToastController();
  const queryParams = new URLSearchParams(window.location.search);
  const urlPreviewParam = queryParams.get('preview') || '';
  const isPreview = urlPreviewParam === '1';
  const history = useHistory();

  const [formState, setFormState] = useState<{
    submitting: boolean;
    valid: boolean;
    values: LeadFormValues;
  }>({
    submitting: false,
    valid: false,
    values: {
      capture_first_name: '',
      capture_last_name: '',
      capture_phone_number: '',
      capture_email: '',
      country: 'United States',
      addressLine1: '',
      addressLine2: '',
      state: '',
      city: '',
      zipCode: '',
      consent: false,
    },
  });

  const handleFormSubmit = async () => {
    trackButtonClicked({ buttonName: 'lf_create_lead' });
    if (formState.valid) {
      if (isPreview) {
        const currentLocation = history.location;
        history.push(`/success${currentLocation.pathname}`);
      } else {
        const response = await mutateAsync(formState.values);
        statusCodesMapper(response, {
          unhandled: () => {
            showToast('Failed to send the form, please try again.');
          },
          success: () => {
            if (data.redirect_url) {
              window.parent.postMessage({ type: 'hcp:redirect', url: data.redirect_url }, '*');
            } else {
              const currentLocation = history.location;
              history.push(`/success${currentLocation.pathname}`);
            }
          },
        });
      }
    }
  };

  const onButtonClick = () => {
    setTimeout(updateHeight, 100);
    // @see https://final-form.org/docs/react-final-form/faq#via-documentgetelementbyid
    formRef?.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const formOnChange = ({ dirty, values, valid }: FormState<LeadFormValues, Partial<LeadFormValues>>) => {
    if (dirty) {
      setFormState((prevState) => ({
        ...prevState,
        values: { ...values },
        valid,
      }));
    }
  };

  useEffect(() => {
    if (data?.fields_to_capture_settings) {
      updateHeight();
    }
  }, [data, updateHeight]);

  if (!data?.fields_to_capture_settings) {
    return null;
  }

  return (
    <>
      <Grid item xs={12}>
        <Form<LeadFormValues> onSubmit={handleFormSubmit} validate={onValidate(data?.fields_to_capture_settings)}>
          {({ values, handleSubmit }) => {
            updateHeight();
            return (
              <form ref={formRef} onSubmit={handleSubmit} aria-label="Contact details">
                <Box mb={2}>
                  <Grid container spacing={1}>
                    {FIELDS_ORDER.map((field) => {
                      const inputValue = data.fields_to_capture_settings[field as keyof LeadCaptureFormFields];

                      if (field === 'capture_phone_number') {
                        return (
                          <Grid item xs={12} key={field}>
                            <FormField<FieldName>
                              mask="phone"
                              autoComplete="tel-national"
                              key={field}
                              name={field as FieldName}
                              data-testid={`lead-form__${field}`}
                              label={FIELD_LABEL_MAP[field as keyof LeadCaptureFormFields]}
                              fullWidth
                            />
                          </Grid>
                        );
                      }
                      if (STALE_LEAD_FORM_FIELDS.includes(field)) {
                        return renderDefaultField(field);
                      }
                      if (!inputValue || DISABLED_FIELDS.includes(field)) {
                        return null;
                      }
                      if (field === 'capture_address') {
                        return (
                          <Grid item xs={12} key={field}>
                            <Box mb={2}>
                              <Typography variant="body1">What is your address?</Typography>
                            </Box>
                            <AddressFields key={field} country={values.country || DEFAULT_COUNTRY} isLeadForm />
                          </Grid>
                        );
                      }
                      if (field === 'capture_custom_question') {
                        return (
                          <Grid item xs={12} key={field}>
                            <FormField<FieldName>
                              key={field}
                              name={field as FieldName}
                              data-testid={`lead-form__${field}`}
                              label={data.fields_to_capture_settings.custom_question_label}
                              fullWidth
                            />
                          </Grid>
                        );
                      }
                      return renderDefaultField(field);
                    })}
                  </Grid>
                </Box>

                <Grid item xs={12}>
                  <Box pt={1.75}>
                    <MarketingConsent label="By checking this box, I consent to receive marketing and promotional text messages at the phone number I have provided above. Message and data rates may apply. Message frequency varies. Reply HELP for help and STOP to stop." />
                  </Box>
                </Grid>

                <FormSpy<LeadFormValues>
                  subscription={{ dirty: true, values: true, valid: true }}
                  onChange={(args) => formOnChange(args)}
                />
              </form>
            );
          }}
        </Form>
      </Grid>
      <Grid item xs={12}>
        <Box py={2}>
          <Grid container spacing={1} direction="column">
            <Grid item xs={12}>
              <Button
                version="v2"
                size="medium"
                fullWidth
                variant="contained"
                color="secondary"
                loading={isLoadingMutation}
                onClick={onButtonClick}
              >
                {data?.text_fields.main_button}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Footer />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </>
  );
};
